'use client';

import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { User, UserCircle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/auth-context';
import { operatorQueries } from '@/features/operators';
import {
  formatDate,
  getAvatarColor,
  getInitials
} from '../utils/operator-avatar';

export function CurrentUserOperatorCard() {
  const t = useTranslations('handoverScanning');
  const { user } = useAuth();

  const userId = user?.id ?? 0;
  const operatorQuery = useQuery({
    ...operatorQueries.detail(userId),
    retry: false
  });

  const operator = operatorQuery.data?.data ?? null;
  const name = user?.name ?? '';

  return (
    <Card className='flex h-full flex-col'>
      <CardHeader className='space-y-3 pb-3'>
        <div className='flex items-center gap-2'>
          <UserCircle className='text-muted-foreground h-5 w-5' />
          <CardTitle className='text-base'>
            {t('panelAssigningToYou')}
          </CardTitle>
        </div>
      </CardHeader>

      <CardContent
        className='flex-1 overflow-y-auto py-4'
        style={{ maxHeight: 'calc(100vh - 350px)' }}
      >
        {operatorQuery.isLoading ? (
          <Skeleton className='h-24 w-full rounded-lg' />
        ) : (
          <div
            className={cn(
              'ring-primary bg-primary/5 w-full rounded-lg border p-3 ring-2'
            )}
          >
            <div className='flex items-start gap-3'>
              <div
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white',
                  name
                    ? getAvatarColor(userId || 1)
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {name ? getInitials(name) : <User className='h-4 w-4' />}
              </div>

              <div className='min-w-0 flex-1 space-y-1'>
                <div className='flex flex-wrap items-center justify-between gap-2'>
                  <div className='flex items-center gap-2'>
                    <p className='text-sm font-semibold'>{name || '—'}</p>
                    <Badge variant='secondary' className='h-5 text-[10px]'>
                      {t('youBadge')}
                    </Badge>
                  </div>
                  {operator && (
                    <span className='text-muted-foreground text-xs'>
                      {t('operatorWorkloadSummary', {
                        containers: operator.pending_containers_count,
                        pages: operator.total_pages_count
                      })}
                    </span>
                  )}
                </div>

                {operator ? (
                  <div className='flex flex-wrap items-center gap-3 text-xs'>
                    {operator.employee_id && (
                      <span className='text-muted-foreground'>
                        {t('employeeId', { id: operator.employee_id })}
                      </span>
                    )}
                    <span className='text-muted-foreground ms-auto'>
                      {t('joinedDate', {
                        date: formatDate(operator.created_at)
                      })}
                    </span>
                  </div>
                ) : (
                  <p className='text-muted-foreground text-xs'>
                    {t('operatorProfileMissing')}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
